import React, { FC } from 'react';
import { List } from '@mui/material';
import { allMemberTypes, TEAM_MEMBER_TYPES, TTeamMemberType } from '../../models/RoleAssignmentModel';
import { toggleListItem } from '../../../../../../../shared/commonFunctions';
import { IRoleAssignmentSelection, ITeamTypeSelection } from '../../models/roleAssignmentSelectionModel';
import { SelectableListItem } from './components/SelectableListItem';
import { TeamSection } from './TeamSection';
import { SelectableCollapse } from './components/SelectableCollapse';

interface ITeamTypeSectionProps {
  teamType: ITeamTypeSelection;
  localState: IRoleAssignmentSelection;

  onModelChange(model: IRoleAssignmentSelection): void;

  onToggleFullTeamType(): void;
}

export const TeamTypeSection: FC<ITeamTypeSectionProps> = ({
  teamType,
  localState,
  onModelChange,
  onToggleFullTeamType,
}) => {
  const shouldIncludeFullTeamType = (type: ITeamTypeSelection) =>
    type.includeManager &&
    type.includedTeamMemberType.length === allMemberTypes.length &&
    type.teams.every((team) => team.includeFullTeam);

  const onToggleManagers = () => {
    localState.teamType = localState.teamType.map((type) => {
      const includeManager = !type.includeManager;
      return type.teamTypeId === teamType.teamTypeId
        ? {
            ...type,
            includeManager,
            includeFullTeamType: shouldIncludeFullTeamType({ ...type, includeManager }),
          }
        : type;
    });

    onModelChange(localState);
  };

  const toggleTeamTypeMemberType = (memberType: TTeamMemberType) => {
    localState.teamType = localState.teamType.map((type) => {
      const includedTeamMemberType = toggleListItem(type.includedTeamMemberType, memberType);
      return type.teamTypeId === teamType.teamTypeId
        ? {
            ...type,
            includedTeamMemberType,
            includeFullTeamType: shouldIncludeFullTeamType({ ...type, includedTeamMemberType }),
          }
        : type;
    });

    onModelChange(localState);
  };

  const onToggleFullTeam = (teamId: string) => {
    localState.teamType = localState.teamType.map((type) => {
      const teams = type.teams.map((team) =>
        team.teamId === teamId
          ? {
              ...team,
              includeFullTeam: !team.includeFullTeam,
              includedTeamMemberType: !team.includeFullTeam ? allMemberTypes : [],
            }
          : team,
      );

      return type.teamTypeId === teamType.teamTypeId
        ? {
            ...type,
            teams,
            includeFullTeamType: shouldIncludeFullTeamType({ ...type, teams }),
          }
        : type;
    });

    onModelChange(localState);
  };

  const onChangeIncludedTeamMemberTypes = (types: TTeamMemberType[], teamId: string) => {
    localState.teamType = localState.teamType.map((type) => {
      const teams = type.teams.map((team) =>
        team.teamId === teamId
          ? {
              ...team,
              includeFullTeam: types.length === allMemberTypes.length,
              includedTeamMemberType: types,
            }
          : team,
      );

      return type.teamTypeId === teamType.teamTypeId
        ? {
            ...type,
            teams,
            includeFullTeamType: shouldIncludeFullTeamType({ ...type, teams }),
          }
        : type;
    });

    onModelChange(localState);
  };

  return (
    <SelectableCollapse
      key={teamType.teamTypeId}
      checked={teamType.includeFullTeamType}
      text={teamType.teamTypeName + ' [Team Type]'}
      onChange={onToggleFullTeamType}
    >
      <List>
        {teamType.teams?.length ? (
          <SelectableCollapse key={teamType.teamTypeId} text={teamType.teamTypeName + ' Teams'}>
            <List>
              {teamType.teams.map((team) => (
                <TeamSection
                  key={team.teamId}
                  team={team}
                  onIncludeFullTeam={() => onToggleFullTeam(team.teamId)}
                  onChangeIncludedMemberTypes={(types) => onChangeIncludedTeamMemberTypes(types, team.teamId)}
                />
              ))}
            </List>
          </SelectableCollapse>
        ) : null}

        <SelectableListItem
          text={teamType.teamTypeName + ' Managers'}
          checked={teamType.includeManager}
          onSelectionChange={onToggleManagers}
        />

        <SelectableListItem
          text={teamType.teamTypeName + ' Directors'}
          checked={teamType.includedTeamMemberType?.indexOf(TEAM_MEMBER_TYPES.Director) >= 0}
          onSelectionChange={() => toggleTeamTypeMemberType(TEAM_MEMBER_TYPES.Director)}
        />

        <SelectableListItem
          text={teamType.teamTypeName + ' Lieutenants'}
          checked={teamType.includedTeamMemberType?.indexOf(TEAM_MEMBER_TYPES.Lieutenant) >= 0}
          onSelectionChange={() => toggleTeamTypeMemberType(TEAM_MEMBER_TYPES.Lieutenant)}
        />

        <SelectableListItem
          text={teamType.teamTypeName + ' Regular Members'}
          checked={teamType.includedTeamMemberType?.indexOf(TEAM_MEMBER_TYPES.Member) >= 0}
          onSelectionChange={() => toggleTeamTypeMemberType(TEAM_MEMBER_TYPES.Member)}
        />
      </List>
    </SelectableCollapse>
  );
};
